import clsx from 'clsx'
import {useFormik} from 'formik'
import {cloneDeep, concat, find, isEmpty, isEqual, size, update} from 'lodash'
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {useCancelToken} from '../../../../../gori/hooks'
import {Button, SelectFormik} from '../../../../../gori/widgets'
import {A1_CONSTANTS} from '../../core/constants'
import {A1BatchService} from '../../core/requests'
import {A1_REQUIRED_FIELDS} from '../../utils'
import {ButtonTemplateMapping} from './ButtonTemplateMapping'
import {SaveTemplateMappingModal} from './SaveTemplateMappingModal'

const MAPPING_FIELDS = A1_CONSTANTS.A1_BATCH_UPLOAD_MAPPING.DEFAULT

const checkRequiredValues = (values: Record<string, string>) => {
  const selected = Object.values(values).filter((value) => value !== '')
  const missingValues = A1_REQUIRED_FIELDS.filter((field) => !selected.includes(field))

  const valueCounts = selected.reduce((acc: Record<string, number>, value) => {
    acc[value] = (acc[value] || 0) + 1
    return acc
  }, {})
  const duplicateValues = Object.keys(valueCounts).filter((value) => valueCounts[value] > 1)

  return {missingValues, duplicateValues}
}

type Props = {
  showModal: boolean
  handleClose: () => void
  handleBack: (mappings: string[]) => void
  data: {columnsCSV: any}
  handleNext: (mappings: string[]) => void
  initialMappings?: string[] | null
}

const A1BatchUploadMappingModal: React.FC<Props> = ({
  showModal,
  handleClose,
  handleBack,
  data,
  handleNext,
  initialMappings = null,
}) => {
  const intl = useIntl()
  const {newCancelToken, isCancel} = useCancelToken()
  const [errors, setErrors] = useState<{missing: any; duplicate: any}>({
    missing: null,
    duplicate: null,
  })
  const [loading, setLoading] = useState<{setAsDefault: boolean; getTemplate: boolean}>({
    setAsDefault: false,
    getTemplate: false,
  })
  const [show, setShow] = useState<{saveTemplate: boolean; hasNext: boolean}>({
    saveTemplate: false,
    hasNext: false,
  })
  const [mappingTemplate, setMappingTemplate] = useState<{options: any; value: any}>({
    options: [],
    value: undefined,
  })
  const [selectedOption, setSelectedOption] = useState<Array<any>>([])
  const templateCurrent = useRef<any>(null)
  const modalBodyRef = useRef<any>(null)

  const handleCloseModal = () => {
    templateCurrent.current = null
    handleClose()
  }

  const initialValues = useCallback(() => {
    if (!data?.columnsCSV) return {}
    return data.columnsCSV.reduce((columns, col, index) => {
      columns[`col_${index}`] = ''
      return columns
    }, {})
  }, [data.columnsCSV])

  const formik = useFormik({
    initialValues: initialValues(),
    enableReinitialize: true,
    onSubmit: async () => {},
  })

  // Pads/truncates a saved mapping to the current file's column count and drops values that
  // are not in the catalog ("Do Not Map").
  const applyMappingValues = (values: string[]) => {
    const requiredLength = size(data.columnsCSV)
    let _values = values?.slice(0, requiredLength) || []
    if (size(_values) < requiredLength) {
      _values = concat(_values, Array(requiredLength - size(_values)).fill(''))
    }
    const columnValues = _values.reduce((acc, item, index) => {
      acc[`col_${index}`] = find(MAPPING_FIELDS, {value: item}) ? item : ''
      return acc
    }, {})
    setSelectedOption(Object.values(columnValues))
    formik.setValues(columnValues)
    return columnValues
  }

  const handleClickTemplate = (dataField) => {
    const templateOptions = applyMappingValues(dataField.value)
    templateCurrent.current = {...dataField, value: templateOptions}
    setMappingTemplate((prev) => ({...prev, value: dataField}))
  }

  const getMappingTemplate = useCallback(
    async ({applyDefaultTemplate}) => {
      try {
        setLoading((prev) => ({...prev, getTemplate: true}))
        const {batch_mappings} = await A1BatchService.getMappingTemplate({
          cancelToken: newCancelToken(),
        })
        if (batch_mappings) {
          setMappingTemplate((prev) => ({...prev, options: batch_mappings}))
          const defaultTemplate = find(batch_mappings, {default: true})
          if (defaultTemplate && applyDefaultTemplate) {
            handleClickTemplate(defaultTemplate)
          }
          const foundTemplate = find(batch_mappings, {id: templateCurrent.current?.id})
          if (foundTemplate && !applyDefaultTemplate) {
            handleClickTemplate(foundTemplate)
          }
        }
      } catch (error) {
        if (isCancel(error)) return
      } finally {
        setLoading((prev) => ({...prev, getTemplate: false}))
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isCancel, newCancelToken, templateCurrent.current]
  )

  // A mapping carried over from a previous visit (the user fixed the CSV and picked it again)
  // wins over the account default template.
  const hasInitialMappings =
    size(initialMappings) === size(data.columnsCSV) && !isEmpty(initialMappings)

  useEffect(() => {
    getMappingTemplate({applyDefaultTemplate: !hasInitialMappings})
    if (hasInitialMappings) {
      applyMappingValues(initialMappings as string[])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const matchField = useCallback((colValue) => {
    return find(
      MAPPING_FIELDS,
      (field: any) =>
        field.value !== '' && (field.value === colValue || (field.aliases || []).includes(colValue))
    )
  }, [])

  const mapByColumnName = useCallback(() => {
    if (!data?.columnsCSV) return {}
    return data.columnsCSV.reduce((columns, col, index) => {
      const matched: any = matchField(col.value)
      const existingValue = matched && Object.values(columns).includes(matched.value)
      columns[`col_${index}`] = matched && !existingValue ? matched.value : ''
      return columns
    }, {})
  }, [data.columnsCSV, matchField])

  const optionsMapping = useMemo(
    () =>
      MAPPING_FIELDS.map((field) => ({
        ...field,
        label: `${intl.formatMessage({id: field.label})}${field.required ? '*' : ''}`,
        isDisabled: !isEmpty(selectedOption) && !!selectedOption.find((col) => col === field.value),
      })),
    [intl, selectedOption]
  )

  const labelOfField = useCallback(
    (value) => {
      const field = find(MAPPING_FIELDS, {value})
      return field ? intl.formatMessage({id: field.label}) : value
    },
    [intl]
  )

  const disableSaveCurrentMapping = useMemo(
    () => isEqual(templateCurrent?.current?.value, formik.values),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formik.values, templateCurrent?.current]
  )

  const handleNextStep = () => {
    setErrors({missing: null, duplicate: null})
    handleNext(Object.values(formik.values))
  }

  const handleUpload = (hasNext: boolean) => {
    const {missingValues, duplicateValues} = checkRequiredValues(formik.values)
    if (isEmpty(missingValues) && isEmpty(duplicateValues)) {
      setErrors({missing: null, duplicate: null})
      if (disableSaveCurrentMapping) {
        if (hasNext) {
          handleNextStep()
        }
      } else {
        setShow({saveTemplate: true, hasNext: hasNext})
      }
    } else {
      if (modalBodyRef.current) {
        modalBodyRef.current.scrollTop = 0
      }
      setErrors({missing: missingValues, duplicate: duplicateValues})
    }
  }

  const handleSetAsDefault = async () => {
    try {
      setLoading((prev) => ({...prev, setAsDefault: true}))
      const res = await A1BatchService.setDefaultTemplate(
        {batch_mapping_id: templateCurrent?.current?.id, default: true},
        {cancelToken: newCancelToken()}
      )
      if (res) {
        getMappingTemplate({applyDefaultTemplate: false})
        toast.success(intl.formatMessage({id: 'THE_TEMPLATE_HAS_BEEN_SUCCESSFULLY_SET_AS_DEFAULT'}))
      }
    } catch (error) {
      if (isCancel(error)) return
    } finally {
      setLoading((prev) => ({...prev, setAsDefault: false}))
    }
  }

  const btnSetAsDefault = useMemo(() => {
    const disabled =
      loading.setAsDefault ||
      templateCurrent.current?.default ||
      isEmpty(mappingTemplate.value) ||
      size(mappingTemplate?.options) < 1
    return {disabled: disabled, active: !disabled}
  }, [loading.setAsDefault, mappingTemplate?.options, mappingTemplate.value])

  const handleDeleteFieldSuccess = ({deleteThis}) => {
    if (deleteThis) {
      templateCurrent.current = undefined
      setMappingTemplate((prev) => ({...prev, value: undefined}))
    }
    getMappingTemplate({applyDefaultTemplate: false})
  }

  const handleMapByColumnName = () => {
    const mappedValues = mapByColumnName()
    formik.setValues(mappedValues)
    setSelectedOption(Object.values(mappedValues))
    const {missingValues, duplicateValues} = checkRequiredValues(mappedValues)
    setErrors({missing: missingValues, duplicate: duplicateValues})
  }

  const handleClickOptionsCol = (option, key) => {
    if (!!selectedOption.find((col) => col === option.value)) return
    setSelectedOption(update(cloneDeep(selectedOption), key, () => option.value))
  }

  return (
    <>
      {show.saveTemplate && (
        <SaveTemplateMappingModal
          show={show.saveTemplate}
          hasNext={show.hasNext}
          handleClose={() => setShow({saveTemplate: false, hasNext: false})}
          data={{
            templateCurrent: templateCurrent,
            mappings: Object.values(formik.values),
            optionsName: isEmpty(mappingTemplate?.options)
              ? []
              : mappingTemplate?.options.map((option) => option.label),
          }}
          handleNextStep={handleNextStep}
          handleGetTemplate={() => getMappingTemplate({applyDefaultTemplate: false})}
        />
      )}
      <Modal
        id='gori_modal_a1_batch_upload_mapping'
        tabIndex={-1}
        aria-hidden='true'
        centered
        dialogClassName='mw-900px h-auto'
        show={showModal}
        backdrop='static'
        onHide={handleCloseModal}
      >
        <div className='modal-content'>
          <Modal.Header closeButton>
            <Modal.Title bsPrefix={'fw-bolder fs-1'}>
              {intl.formatMessage({id: 'MAP_YOUR_CSV_TO_SHIPBAE'})}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body
            className={clsx('vh-75 scroll-y', {'cursor-no-drop': loading.getTemplate})}
            ref={modalBodyRef}
          >
            <div className={clsx({'pe-none opacity-75': loading.getTemplate})}>
              <div className='d-flex align-items-stretch mb-8'>
                <ButtonTemplateMapping
                  loadingParent={{getTemplate: loading.getTemplate}}
                  mappingTemplate={mappingTemplate}
                  handleClickTemplate={handleClickTemplate}
                  handleDeleteFieldSuccess={handleDeleteFieldSuccess}
                />
                <div className='col-3 ms-4 d-flex align-items-center'>
                  <Button
                    divClassName='w-100'
                    // Outline with a primary border: btn-outline keeps the border (plain .btn
                    // removes it), btn-active-light-primary forces it primary and paints a
                    // light-primary background on hover. Disabled dims it via Bootstrap.
                    className={clsx(
                      'btn btn-outline btn-active-light-primary text-primary w-100 text-center lh-sm py-2 px-3',
                      {'cursor-no-drop': btnSetAsDefault.disabled}
                    )}
                    event={() => btnSetAsDefault.active && handleSetAsDefault()}
                    disabled={btnSetAsDefault.disabled}
                    loading={loading.setAsDefault}
                    loadingText={intl.formatMessage({id: 'SET_THIS_TEMPLATE_AS_DEFAULT'})}
                    label={intl.formatMessage({id: 'SET_THIS_TEMPLATE_AS_DEFAULT'})}
                  />
                </div>
              </div>
              <div className='border rounded-2 px-10 py-8'>
                <div className='d-flex'>
                  <Button
                    className='btn btn-outline btn-active-light-primary text-primary fw-bolder'
                    event={handleMapByColumnName}
                    label={intl.formatMessage({id: 'MAP_BY_COLUMN_NAME'})}
                  />
                  <Button
                    divClassName='ms-3'
                    className={clsx(
                      'btn btn-outline btn-active-light-primary text-primary fw-bolder',
                      {
                        'cursor-no-drop': disableSaveCurrentMapping,
                      }
                    )}
                    disabled={disableSaveCurrentMapping}
                    event={() => !disableSaveCurrentMapping && handleUpload(false)}
                    label={intl.formatMessage({id: 'SAVE_CURRENT_MAPPING'})}
                  />
                </div>
                {(!isEmpty(errors.missing) || !isEmpty(errors.duplicate)) && (
                  <div className='text-danger my-4'>
                    {!isEmpty(errors.missing) && (
                      <div className='my-2'>
                        {intl.formatMessage(
                          {id: 'REQUIRED_FIELD_IS_MISSING_ERRORS'},
                          {errors: errors.missing.map((error) => labelOfField(error)).join(', ')}
                        )}
                      </div>
                    )}
                    {!isEmpty(errors.duplicate) && (
                      <div className='my-2'>
                        {intl.formatMessage(
                          {id: 'YOU_SELECTED_THE_SAME_VALUE_MORE_THAN_ONCE'},
                          {errors: errors.duplicate.map((error) => labelOfField(error)).join(', ')}
                        )}
                      </div>
                    )}
                  </div>
                )}
                <div className='d-flex mt-10'>
                  <div className='col-6 fw-bold fs-5'>
                    {intl.formatMessage({id: 'COLUMNS_IN_YOUR_CSV'})}
                  </div>
                  <div className='col-6 fw-bold fs-5'>
                    {intl.formatMessage({id: 'SHIPBAE_FIELDS'})}
                  </div>
                </div>
                <hr className='bg-gray-600' />
                {!isEmpty(data.columnsCSV) &&
                  data.columnsCSV?.map((col, key) => (
                    <div key={key}>
                      <div className='d-flex'>
                        <SelectFormik
                          className='col-6'
                          labelClassName='col-6 col-form-label fw-bolder fs-5'
                          label={col.label}
                          emptyDefault={false}
                          options={optionsMapping}
                          name={`col_${key}`}
                          formik={formik}
                          checkFormik={false}
                          placeholder={intl.formatMessage({id: 'DO_NOT_MAP'})}
                          onChange={(option) => handleClickOptionsCol(option, key)}
                        />
                      </div>
                      <hr className='bg-gray-600' />
                    </div>
                  ))}
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className='d-flex justify-content-start flex-fill'>
              <Button
                className='btn btn-secondary'
                label={intl.formatMessage({id: 'BACK'})}
                event={() => handleBack(Object.values(formik.values))}
              />
            </div>
            <div className='d-flex justify-content-end'>
              <Button
                className='btn btn-secondary me-2'
                label={intl.formatMessage({id: 'CANCEL'})}
                event={handleCloseModal}
              />
              <Button
                className='btn btn-primary'
                label={intl.formatMessage({id: 'NEXT'})}
                event={() => handleUpload(true)}
              />
            </div>
          </Modal.Footer>
        </div>
      </Modal>
    </>
  )
}

export {A1BatchUploadMappingModal}
